import React, { useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	FormControl,
	Select,
	MenuItem,
	Chip,
	Checkbox,
	ListItemText,
	OutlinedInput,
	CircularProgress,
	TextField,
	InputAdornment,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Box,
} from "@mui/material";
import { Search, Add, Close, Edit, Delete, KeyboardArrowDown } from "@mui/icons-material";
import {
	formPrimary,
	getChevronReserve,
	selectChevronCapClassName,
	selectChevronCompact,
} from "@config/theme/formControls";

/** My Skills select only — slightly smaller than default portal chevron */
const SKILLS_SELECT_CHEVRON_RESERVE = getChevronReserve(40);
import Button from "@components/common/Button/Button";
import { useSkills, useCreateSkill, type Skill } from "@features/Jobs/services/skillsQueries";
import {
	useStudentSkills,
	useAddStudentSkill,
	useUpdateStudentSkill,
	useDeleteStudentSkill,
} from "@features/Profile/services/StudentSkills";
import type { StudentSkill } from "@features/Profile/services/StudentSkills/queries";
import { UserModal } from "@components/common/Modal/UserModal";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";

interface AddSkillsModalProps {
	open: boolean;
	onClose: () => void;
}

const AddSkillsModal: React.FC<AddSkillsModalProps> = ({ open, onClose }) => {
	const { t } = useTranslation();
	const { data: jobSkills = [], isLoading: loadingJobSkills } = useSkills();
	const createSkillMutation = useCreateSkill();
	const { data: studentSkills = [], isLoading: loadingStudentSkills } = useStudentSkills(open);
	const addStudentSkillMutation = useAddStudentSkill();
	const updateStudentSkillMutation = useUpdateStudentSkill();
	const deleteStudentSkillMutation = useDeleteStudentSkill();

	const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
	const [skillSearchTerm, setSkillSearchTerm] = useState<string>("");
	const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
	const [editingSkillUuid, setEditingSkillUuid] = useState<string>("");

	// Ref for the search input to maintain focus
	const searchInputRef = useRef<HTMLInputElement>(null);
	const skillsSelectRef = useRef<HTMLDivElement>(null);

	const openSkillsSelect = useCallback(() => {
		const root = skillsSelectRef.current;
		if (!root) return;
		const combo = root.querySelector('[role="combobox"]') as HTMLElement | null;
		const target = combo ?? root.querySelector(".MuiSelect-select");
		if (!target) return;
		target.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }));
		(target as HTMLElement).click();
	}, []);

	// Handle keyboard events to prevent focus loss
	const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
		// Prevent all keyboard events from bubbling to the Select component
		e.stopPropagation();
	}, []);

	// Add Skill Modal state
	const [addSkillModalOpen, setAddSkillModalOpen] = useState(false);
	const [newSkillName, setNewSkillName] = useState("");
	const [newSkillError, setNewSkillError] = useState("");

	// Store newly created skills temporarily (before jobSkills refetches)
	const [newlyCreatedSkills, setNewlyCreatedSkills] = useState<Map<string, string>>(new Map());

	// Open Add Skill Modal
	const openAddSkillModal = () => {
		setNewSkillName(skillSearchTerm.trim());
		setNewSkillError("");
		setAddSkillModalOpen(true);
	};

	// Close Add Skill Modal
	const closeAddSkillModal = () => {
		setAddSkillModalOpen(false);
		setNewSkillName("");
		setNewSkillError("");
	};

	// Handle adding a new skill from modal
	const handleAddNewSkill = async () => {
		if (!newSkillName.trim()) {
			setNewSkillError("Skill name is required");
			return;
		}

		// Check if skill already exists
		const skillExists = jobSkills.some((skill: Skill) => {
			const skillName = (skill.name || "").toLowerCase();
			return skillName === newSkillName.toLowerCase().trim();
		});

		if (skillExists) {
			setNewSkillError("This skill already exists");
			return;
		}

		try {
			const payload = {
				name: newSkillName.trim(),
				description: `${newSkillName.trim()} skill`,
				isActive: true,
			};
			const skillNameToAdd = newSkillName.trim();
			const result = await createSkillMutation.mutateAsync(payload);
			if (result?.uuid) {
				// Add the new skill to selected skills
				const newSkillId = result.uuid;
				setSelectedSkills(prev => [...prev, newSkillId]);
				// Store the skill name temporarily for display
				setNewlyCreatedSkills(prev => new Map(prev).set(newSkillId, skillNameToAdd));
			}
			// Show success notification
			showNotification({
				message: "Skill added successfully",
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
			// Close modal and clear search term
			closeAddSkillModal();
			setSkillSearchTerm("");
		} catch (error) {
			console.error("Error adding skill:", error);
			// Handle Axios error response or fallback to generic message
			const axiosError = error as { response?: { data?: { message?: string } } };
			const errorMessage = axiosError?.response?.data?.message || "Failed to add skill. Please try again.";
			setNewSkillError(errorMessage);
		}
	};

	// Handle skill selection change - update selected skills (don't add immediately)
	const handleSkillChange = (event: { target: { value: unknown } }) => {
		const value = event.target.value;
		const newSelected = typeof value === "string" ? value.split(",") : (value as string[]);
		// Filter out empty strings to prevent space issues
		const filteredSelected = newSelected.filter(v => v && v.trim() !== "");
		setSelectedSkills(filteredSelected);
	};

	// Handle adding selected skills
	const handleAddSelectedSkills = async () => {
		if (selectedSkills.length === 0) {
			showNotification({
				message: "Please select at least one skill to add",
				severity: SNACKBAR_SEVERITY.WARNING,
			});
			return;
		}

		// Find newly added skills (not in existing studentSkills)
		const newlyAdded = selectedSkills.filter(skillUuid => !existingSkillUuids.includes(skillUuid));

		if (newlyAdded.length === 0) {
			showNotification({
				message: "All selected skills are already added",
				severity: SNACKBAR_SEVERITY.WARNING,
			});
			return;
		}

		try {
			// Send all selected skills in a single API call
			await addStudentSkillMutation.mutateAsync({ skillUuids: newlyAdded });
			// Success notification is handled in the mutation's onSuccess
			
			// Clear selection after successful addition
			setSelectedSkills([]);
			setSkillSearchTerm("");
			setNewlyCreatedSkills(new Map());
		} catch (error) {
			// Error notification is handled in the mutation's onError
			console.error("Error adding skills:", error);
		}
	};

	// Get existing skill UUIDs
	const existingSkillUuids = useMemo(() => {
		return studentSkills.map((skill: StudentSkill) => skill.skillUuid || skill.skill?.uuid).filter(Boolean) as string[];
	}, [studentSkills]);

	// Filter out already added skills from dropdown options
	const availableSkills = useMemo(() => {
		return jobSkills.filter((skill: { uuid?: string; id?: number; name?: string }) => {
			const skillId = skill.uuid || skill.id?.toString() || "";
			return !existingSkillUuids.includes(skillId);
		});
	}, [jobSkills, existingSkillUuids]);

	// Filter available skills based on search term
	const filteredAvailableSkills = useMemo(() => {
		if (!skillSearchTerm.trim()) return availableSkills;
		return availableSkills.filter((skill: Skill) => {
			const skillName = (skill.name || "").toLowerCase();
			return skillName.includes(skillSearchTerm.toLowerCase());
		});
	}, [availableSkills, skillSearchTerm]);

	// Combine filtered available skills with newly created skills for dropdown display
	const combinedDropdownSkills = useMemo(() => {
		// Get newly created skills that are not yet in jobSkills
		const newSkillsArray: { uuid: string; name: string }[] = [];
		newlyCreatedSkills.forEach((name, uuid) => {
			// Check if this skill is not already in jobSkills (to avoid duplicates after refetch)
			const existsInJobSkills = jobSkills.some(s => s.uuid === uuid);
			if (!existsInJobSkills) {
				// Apply search filter to newly created skills too
				if (!skillSearchTerm.trim() || name.toLowerCase().includes(skillSearchTerm.toLowerCase())) {
					newSkillsArray.push({ uuid, name });
				}
			}
		});
		return [...filteredAvailableSkills, ...newSkillsArray];
	}, [filteredAvailableSkills, newlyCreatedSkills, jobSkills, skillSearchTerm]);

	// Handle modal close
	const handleClose = () => {
		setSelectedSkills([]);
		setSkillSearchTerm("");
		setNewlyCreatedSkills(new Map());
		onClose();
	};

	return (
		<>
			<UserModal heigth={100} width={50} show={open} onClose={handleClose}>
				<div className="p-6 bg-white">
					{/* Header */}
					<div className="mb-6">
						<h2 className="text-2xl font-bold text-gray-900">{t("common.ui.mySkills")}</h2>
						<p className="text-sm text-gray-600 mt-2">
							Manage your skills - view, edit, or remove existing skills, or add new ones
						</p>
					</div>

					{/* My Current Skills Section */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-3">{t("common.ui.myCurrentSkills")}</h3>
						{loadingStudentSkills ? (
							<div className="flex justify-center items-center py-8">
								<CircularProgress size={24} />
							</div>
						) : studentSkills.length > 0 ? (
							<div className="space-y-2">
								{studentSkills.map((studentSkill: StudentSkill) => {
									const skillUuid = studentSkill.skillUuid || studentSkill.skill?.uuid || "";
									const skillName = studentSkill.skill?.name || "Unknown Skill";
									const isEditing = editingSkillId === studentSkill.uuid;

									return (
										<div
											key={studentSkill.uuid}
											className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
											{isEditing ? (
												<div className="flex-1 flex items-center gap-2">
													<FormControl size="small" className="flex-1">
														<Select
															value={editingSkillUuid || skillUuid}
															onChange={e => setEditingSkillUuid(e.target.value)}
															displayEmpty
															sx={{ height: "36px" }}>
															{jobSkills.map((skill: { uuid?: string; id?: number; name?: string }) => {
																const id = skill.uuid || skill.id?.toString() || "";
																return (
																	<MenuItem key={id} value={id}>
																		{skill.name}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
													<Button
														variant="primary"
														onClick={async () => {
															try {
																await updateStudentSkillMutation.mutateAsync({
																	uuid: studentSkill.uuid,
																	payload: { skillUuid: editingSkillUuid || skillUuid },
																});
																// Success notification is handled in the mutation's onSuccess
																setEditingSkillId(null);
																setEditingSkillUuid("");
															} catch (error) {
																// Error notification is handled in the mutation's onError
																// Just log for debugging
																console.error("Error updating skill:", error);
																// Don't close edit mode on error so user can retry
															}
														}}
														disabled={updateStudentSkillMutation.isPending}>
														{updateStudentSkillMutation.isPending ? "Saving..." : "Save"}
													</Button>
													<Button
														variant="tertiary"
														onClick={() => {
															setEditingSkillId(null);
															setEditingSkillUuid("");
														}}>
														Cancel
													</Button>
												</div>
											) : (
												<>
													<span className="text-gray-900 font-medium flex-1">{skillName}</span>
													<div className="flex items-center gap-2">
														<IconButton
															size="small"
															onClick={() => {
																setEditingSkillId(studentSkill.uuid);
																setEditingSkillUuid(skillUuid);
															}}
															className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
															disabled={updateStudentSkillMutation.isPending || deleteStudentSkillMutation.isPending}>
															<Edit sx={{ fontSize: "18px" }} />
														</IconButton>
														<IconButton
															size="small"
															onClick={async () => {
																if (window.confirm(`Are you sure you want to remove "${skillName}"?`)) {
																	try {
																		await deleteStudentSkillMutation.mutateAsync(studentSkill.uuid);
																		// Success notification is handled in the mutation's onSuccess
																	} catch (error) {
																		// Error notification is handled in the mutation's onError
																		// Just log for debugging
																		console.error("Error deleting skill:", error);
																	}
																}
															}}
															className="text-red-600 hover:text-red-700 hover:bg-red-50"
															disabled={updateStudentSkillMutation.isPending || deleteStudentSkillMutation.isPending}>
															{deleteStudentSkillMutation.isPending ? (
																<CircularProgress size={18} />
															) : (
																<Delete sx={{ fontSize: "18px" }} />
															)}
														</IconButton>
													</div>
												</>
											)}
										</div>
									);
								})}
							</div>
						) : (
							<div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
								<p className="text-gray-500">{t("common.ui.noSkillsYet")}</p>
							</div>
						)}
					</div>

					{/* Divider */}
					<div className="h-px bg-gray-200 my-6" />

					{/* Add New Skills Section */}
					<div>
						<h3 className="text-lg font-semibold text-gray-900 mb-3">{t("common.ui.addNewSkills")}</h3>
						<div>
							<label className="mb-2 block text-[1rem] font-semibold text-gray-700">{t("common.ui.selectSkillsToAdd")}</label>
							<div ref={skillsSelectRef} className="relative flex w-full items-center">
							<FormControl fullWidth>
								<Select
									IconComponent={() => null}
									multiple
									value={selectedSkills}
									onChange={handleSkillChange}
									disabled={
										loadingJobSkills ||
										loadingStudentSkills ||
										createSkillMutation.isPending ||
										addStudentSkillMutation.isPending
									}
									input={<OutlinedInput />}
									onOpen={() => {
										// Focus the search input when dropdown opens
										setTimeout(() => {
											searchInputRef.current?.focus();
										}, 100);
									}}
									renderValue={selected => (
										<div className="flex flex-wrap gap-1">
											{Array.isArray(selected) &&
												selected.map(value => {
													// Find the skill name from jobSkills array or newly created skills
													const skill = jobSkills.find(s => (s.uuid || s.id?.toString()) === value);
													const skillName = skill?.name || newlyCreatedSkills.get(value) || value;
													return (
														<Chip
															key={value}
															label={skillName}
															size="small"
															sx={{
																backgroundColor: "#f3f4f6",
																color: "#374151",
															}}
														/>
													);
												})}
										</div>
									)}
									sx={{
										borderRadius: 1,
										alignItems: "center",
										"& .MuiOutlinedInput-root": {
											paddingRight: `${SKILLS_SELECT_CHEVRON_RESERVE}px !important`,
											display: "flex",
											alignItems: "center",
										},
										"& .MuiSelect-select": {
											padding: "8px 12px",
										},
										"& .MuiSelect-icon": {
											display: "none",
										},
									}}
									MenuProps={{
										disableScrollLock: false,
										sx: { zIndex: 10001 },
										PaperProps: {
											sx: {
												zIndex: 10001,
												borderRadius: 2,
												boxShadow: "0 2px 8px 0 rgba(16, 24, 40, 0.04)",
												mt: 1,
												maxHeight: 350,
												backgroundColor: "#f9fafb",
											},
										},
									}}>
									{/* Search Input - Using Box instead of MenuItem to prevent selection */}
									<Box
										sx={{
											position: "sticky",
											top: 0,
											backgroundColor: "white",
											zIndex: 1,
											padding: "8px 16px",
										}}
										onKeyDown={handleSearchKeyDown}
										onClick={e => e.stopPropagation()}>
										<TextField
											inputRef={searchInputRef}
											size="small"
											placeholder={t("common.form.placeholders.searchSkills")}
											variant="outlined"
											value={skillSearchTerm}
											onChange={e => {
												e.stopPropagation();
												setSkillSearchTerm(e.target.value);
											}}
											fullWidth
											autoFocus
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<Search sx={{ fontSize: "20px", color: "#9CA3AF" }} />
													</InputAdornment>
												),
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f9fafb",
													fontSize: "13px",
													"& fieldset": { borderColor: "#e5e7eb" },
													"&:hover fieldset": { borderColor: "#d1d5db" },
													"&.Mui-focused fieldset": { borderColor: "#3b82f6" },
												},
											}}
											onClick={e => {
												e.stopPropagation();
												// Ensure focus stays on the input
												searchInputRef.current?.focus();
											}}
											onKeyDown={handleSearchKeyDown}
											onMouseDown={e => e.stopPropagation()}
											autoComplete="off"
										/>
									</Box>

									{/* Add New Skill Button - Always visible */}
									<MenuItem
										onClick={e => {
											e.stopPropagation();
											e.preventDefault();
											openAddSkillModal();
										}}
										sx={{
											fontFamily: "Poppins, sans-serif",
											backgroundColor: "#BA2E5D",
											margin: "4px 8px",
											borderRadius: "6px",
											padding: "10px 12px",
											color: "#ffffff",
											"&:hover": {
												backgroundColor: "#2a2a2a",
											},
										}}>
										<Add sx={{ color: "#ffffff", mr: 1, fontSize: "20px" }} />
										<ListItemText
											primary="+ Add New Skill"
											sx={{
												"& .MuiListItemText-primary": {
													fontFamily: "Poppins, sans-serif",
													fontSize: "0.875rem",
													color: "#ffffff",
													fontWeight: 600,
												},
											}}
										/>
									</MenuItem>

								{loadingJobSkills ? (
									<MenuItem disabled>
										<div className="flex items-center gap-2">
											<CircularProgress size={16} />
											<span className="text-sm">{t("common.ui.loadingSkills")}</span>
										</div>
									</MenuItem>
								) : combinedDropdownSkills.length > 0 ? (
									combinedDropdownSkills.map((skill: { uuid?: string; id?: number; name?: string }) => {
										const skillId = skill.uuid || skill.id?.toString() || "";
										const isSelected = selectedSkills.indexOf(skillId) > -1;
										return (
											<MenuItem
												key={skillId}
												value={skillId}
												sx={{
													fontFamily: "Poppins, sans-serif",
													backgroundColor: "#ffffff",
													margin: "2px 8px",
													borderRadius: "4px",
													borderBottom: isSelected ? "none" : "1px solid #f1f5f9",
													"&:hover": {
														backgroundColor: "#f5f5f5",
														color: "#1e293b",
													},
													"&.Mui-selected": {
														backgroundColor: "#BA2E5D",
														color: "#ffffff",
														borderBottom: "none",
														"& .MuiListItemText-primary": {
															color: "#ffffff",
														},
													},
													"&.Mui-selected:hover": {
														backgroundColor: "#BA2E5D",
														color: "#ffffff",
														"& .MuiListItemText-primary": {
															color: "#ffffff",
														},
													},
													"&.Mui-focusVisible": {
														backgroundColor: "#f5f5f5",
													},
													"&:last-child": {
														borderBottom: "none",
													},
												}}>
												<Checkbox
													checked={isSelected}
													sx={{
														color: "#94a3b8",
														"&.Mui-checked": {
															color: "#ffffff",
														},
														"&:hover": {
															backgroundColor: "transparent",
														},
													}}
												/>
												<ListItemText
													primary={skill.name}
													sx={{
														"& .MuiListItemText-primary": {
															fontFamily: "Poppins, sans-serif",
															fontSize: "0.875rem",
														},
													}}
												/>
											</MenuItem>
										);
									})
								) : skillSearchTerm && combinedDropdownSkills.length === 0 ? (
									<MenuItem disabled>
										<span className="text-sm text-gray-500">
											No skills found - Click "Add New Skill" above to add
										</span>
									</MenuItem>
								) : !skillSearchTerm && availableSkills.length === 0 && newlyCreatedSkills.size === 0 ? (
									<MenuItem disabled>
										<span className="text-sm text-gray-500">{t("common.ui.allSkillsAdded")}</span>
									</MenuItem>
								) : null}
								</Select>
							</FormControl>
							<button
								type="button"
								className={`${selectChevronCapClassName} !top-1/2 !-translate-y-1/2 flex items-center justify-center`}
								style={{
									width: selectChevronCompact.width,
									height: selectChevronCompact.height,
									borderRadius: selectChevronCompact.borderRadius,
									right: selectChevronCompact.rightInset,
									backgroundColor: formPrimary,
								}}
								onClick={openSkillsSelect}
								onMouseDown={e => e.preventDefault()}
								aria-label="Open skills list"
								tabIndex={-1}>
								<KeyboardArrowDown sx={{ color: "#fff", fontSize: 14, pointerEvents: "none" }} />
							</button>
						</div>
						</div>
					</div>

					{/* Footer */}
					<div className="mt-6 flex justify-end gap-4">
						<Button
							onClick={handleClose}
							variant="tertiary"
							disabled={
								addStudentSkillMutation.isPending ||
								updateStudentSkillMutation.isPending ||
								deleteStudentSkillMutation.isPending
							}>
							Close
						</Button>
						<Button
							onClick={handleAddSelectedSkills}
							variant="primary"
							disabled={
								addStudentSkillMutation.isPending ||
								updateStudentSkillMutation.isPending ||
								deleteStudentSkillMutation.isPending ||
								selectedSkills.length === 0
							}>
							{addStudentSkillMutation.isPending ? (
								<div className="flex items-center gap-2">
									<CircularProgress size={16} sx={{ color: "white" }} />
									<span>{t("common.ui.adding")}</span>
								</div>
							) : (
								"Add"
							)}
						</Button>
					</div>
				</div>
			</UserModal>

			{/* Add New Skill Modal */}
			<Dialog
				open={addSkillModalOpen}
				onClose={closeAddSkillModal}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "12px",
						padding: "8px",
					},
				}}>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						fontFamily: "Poppins, sans-serif",
						fontWeight: 600,
						fontSize: "1.25rem",
						color: "#1e293b",
						pb: 1,
					}}>
					Add New Skill
					<IconButton onClick={closeAddSkillModal} size="small">
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<div className="mt-2">
						<label className="mb-2 block text-[1rem] font-semibold text-gray-700">
							Skill Name <span className="text-red-500">*</span>
						</label>
						<TextField
							fullWidth
							size="small"
							placeholder={t("common.form.placeholders.enterSkillName")}
							value={newSkillName}
							onChange={e => {
								setNewSkillName(e.target.value);
								setNewSkillError("");
							}}
							error={!!newSkillError}
							helperText={newSkillError}
							autoFocus
							sx={{
								"& .MuiOutlinedInput-root": {
									fontFamily: "Poppins, sans-serif",
									fontSize: "13px",
									borderRadius: "8px",
									"& fieldset": { borderColor: "#e5e7eb" },
									"&:hover fieldset": { borderColor: "#d1d5db" },
									"&.Mui-focused fieldset": { borderColor: "#BA2E5D" },
								},
							}}
							onKeyDown={e => {
								if (e.key === "Enter" && !createSkillMutation.isPending) {
									handleAddNewSkill();
								}
							}}
						/>
					</div>
				</DialogContent>
				<DialogActions sx={{ padding: "16px 24px", gap: 1 }}>
					<Button onClick={closeAddSkillModal} variant="tertiary" disabled={createSkillMutation.isPending}>
						Cancel
					</Button>
					<Button
						onClick={handleAddNewSkill}
						variant="primary"
						disabled={createSkillMutation.isPending || !newSkillName.trim()}>
						{createSkillMutation.isPending ? (
							<div className="flex items-center gap-2">
								<CircularProgress size={16} sx={{ color: "white" }} />
								<span>{t("common.ui.adding")}</span>
							</div>
						) : (
							"Add Skill"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddSkillsModal;
